/**
 * Lightweight markdown-to-React renderer for assistant bubbles.
 * Security invariant: emit React nodes only, never raw HTML strings.
 */

import { Fragment, createElement, type ReactNode } from 'react';

type InlineToken =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'italic'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; label: string; href: string; external: boolean };

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const HEADING_CLASS: Record<HeadingLevel, string> = {
  1: 'text-2xl font-display font-semibold mt-3 mb-2',
  2: 'text-xl font-display font-semibold mt-3 mb-2',
  3: 'text-lg font-display font-semibold mt-2 mb-1',
  4: 'text-base font-semibold mt-2 mb-1',
  5: 'text-sm font-semibold mt-2 mb-1',
  6: 'text-sm font-semibold mt-2 mb-1 text-ink-2',
};

function sanitizeHref(rawHref: string): { href: string; external: boolean } | null {
  const href = rawHref.trim();
  if (!href) return null;
  if (href.startsWith('//')) return null;
  if (href.startsWith('/')) return { href, external: false };
  if (href.startsWith('./') || href.startsWith('../')) return { href, external: false };
  if (href.startsWith('#')) return { href, external: false };
  if (/^https?:\/\//i.test(href)) return { href, external: true };
  return null;
}

function parseLinkToken(line: string, start: number): { token: InlineToken; nextIndex: number } | null {
  if (line[start] !== '[') return null;
  const labelEnd = line.indexOf(']', start + 1);
  if (labelEnd <= start + 1) return null;
  if (line[labelEnd + 1] !== '(') return null;
  const hrefStart = labelEnd + 2;
  let hrefEnd = -1;
  let nestedParens = 0;
  for (let i = hrefStart; i < line.length; i++) {
    const char = line[i];
    if (char === '(') {
      nestedParens++;
      continue;
    }
    if (char === ')') {
      if (nestedParens === 0) {
        hrefEnd = i;
        break;
      }
      nestedParens--;
    }
  }
  if (hrefEnd <= hrefStart) return null;

  const label = line.slice(start + 1, labelEnd);
  const href = line.slice(hrefStart, hrefEnd);
  const sanitized = sanitizeHref(href);
  if (!sanitized) {
    return {
      token: { type: 'text', value: line.slice(start, hrefEnd + 1) },
      nextIndex: hrefEnd + 1,
    };
  }

  return {
    token: { type: 'link', label, href: sanitized.href, external: sanitized.external },
    nextIndex: hrefEnd + 1,
  };
}

function tokenizeInline(line: string): InlineToken[] {
  const out: InlineToken[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '`') {
      const end = line.indexOf('`', i + 1);
      if (end > i + 1) {
        out.push({ type: 'code', value: line.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (line.startsWith('**', i)) {
      const end = line.indexOf('**', i + 2);
      if (end > i + 2) {
        out.push({ type: 'bold', value: line.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    if (line[i] === '*') {
      const end = line.indexOf('*', i + 1);
      if (end > i + 1) {
        out.push({ type: 'italic', value: line.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (line[i] === '[') {
      const parsedLink = parseLinkToken(line, i);
      if (parsedLink) {
        out.push(parsedLink.token);
        i = parsedLink.nextIndex;
        continue;
      }
    }

    let j = i;
    while (j < line.length && line[j] !== '*' && line[j] !== '`' && line[j] !== '[') j++;
    if (j === i) {
      out.push({ type: 'text', value: line[i] ?? '' });
      i++;
      continue;
    }
    out.push({ type: 'text', value: line.slice(i, j) });
    i = j;
  }
  return out;
}

function renderInline(line: string, keyPrefix: string): ReactNode[] {
  const tokens = tokenizeInline(line);
  return tokens.map((t, i) => {
    const key = `${keyPrefix}-${i}`;
    if (t.type === 'bold') return createElement('strong', { key }, t.value);
    if (t.type === 'italic') return createElement('em', { key }, t.value);
    if (t.type === 'code') {
      return createElement('code', { key, className: 'font-mono text-sm bg-inset px-1 py-0.5 rounded' }, t.value);
    }
    if (t.type === 'link') {
      return createElement(
        'a',
        {
          key,
          href: t.href,
          className: 'text-primary underline break-words',
          target: t.external ? '_blank' : undefined,
          rel: t.external ? 'noopener noreferrer' : undefined,
        },
        t.label,
      );
    }
    return createElement(Fragment, { key }, t.value);
  });
}

function headingClassName(level: HeadingLevel): string {
  return HEADING_CLASS[level];
}

function parseTableRow(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return null;
  const cells = trimmed.split('|').map((cell) => cell.trim());
  if (cells[0] === '') cells.shift();
  if (cells[cells.length - 1] === '') cells.pop();
  if (cells.length < 2) return null;
  return cells;
}

function isTableSeparatorRow(line: string, columnCount: number): boolean {
  const cells = parseTableRow(line);
  if (!cells || cells.length !== columnCount) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

export function renderMarkdown(input: string): ReactNode[] {
  if (!input) return [];
  const lines = input.split('\n');
  const out: ReactNode[] = [];
  let lineIndex = 0;
  let bulletGroup: ReactNode[] = [];
  let numberedGroup: ReactNode[] = [];

  function flushBullet() {
    if (bulletGroup.length) {
      out.push(createElement('ul', { key: `ul-${out.length}`, className: 'list-disc pl-5 my-2' }, ...bulletGroup));
      bulletGroup = [];
    }
  }
  function flushNumbered() {
    if (numberedGroup.length) {
      out.push(createElement('ol', { key: `ol-${out.length}`, className: 'list-decimal pl-5 my-2' }, ...numberedGroup));
      numberedGroup = [];
    }
  }
  function flushLists() {
    flushBullet();
    flushNumbered();
  }

  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? '';

    if (line.startsWith('```')) {
      flushLists();

      const codeLines: string[] = [];
      lineIndex++;
      while (lineIndex < lines.length && !(lines[lineIndex] ?? '').startsWith('```')) {
        codeLines.push(lines[lineIndex] ?? '');
        lineIndex++;
      }
      if (lineIndex < lines.length && (lines[lineIndex] ?? '').startsWith('```')) {
        lineIndex++;
      }

      out.push(
        createElement(
          'pre',
          { key: `pre-${out.length}`, className: 'my-2 overflow-x-auto rounded-md bg-inset p-3' },
          createElement('code', { className: 'font-mono text-sm whitespace-pre' }, codeLines.join('\n')),
        ),
      );
      continue;
    }

    const headerCells = line.includes('|') ? parseTableRow(line) : null;
    const separatorLine = lines[lineIndex + 1] ?? '';
    if (headerCells && isTableSeparatorRow(separatorLine, headerCells.length)) {
      flushLists();

      const rows: string[][] = [];
      lineIndex += 2;
      while (lineIndex < lines.length) {
        const row = parseTableRow(lines[lineIndex] ?? '');
        if (!row || row.length !== headerCells.length) break;
        rows.push(row);
        lineIndex++;
      }

      out.push(
        createElement(
          'div',
          { key: `table-wrap-${out.length}`, className: 'my-2 overflow-x-auto' },
          createElement(
            'table',
            { className: 'w-full border-collapse text-sm' },
            createElement(
              'thead',
              null,
              createElement(
                'tr',
                null,
                ...headerCells.map((cell, i) =>
                  createElement(
                    'th',
                    {
                      key: `th-${i}`,
                      className: 'border-b border-card-line px-2 py-1 text-left font-semibold',
                    },
                    ...renderInline(cell, `th-${lineIndex}-${i}`),
                  ),
                ),
              ),
            ),
            createElement(
              'tbody',
              null,
              ...rows.map((row, rowIndex) =>
                createElement(
                  'tr',
                  { key: `tr-${rowIndex}`, className: 'border-b border-card-line/60 last:border-b-0' },
                  ...row.map((cell, cellIndex) =>
                    createElement(
                      'td',
                      { key: `td-${rowIndex}-${cellIndex}`, className: 'px-2 py-1 align-top' },
                      ...renderInline(cell, `td-${rowIndex}-${cellIndex}`),
                    ),
                  ),
                ),
              ),
            ),
          ),
        ),
      );
      continue;
    }

    const headingMatch = line.startsWith('#') ? line.match(/^(#{1,6})\s+(.+)$/) : null;
    if (headingMatch && headingMatch[2] !== undefined) {
      flushLists();
      const level = headingMatch[1]!.length as HeadingLevel;
      const tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      out.push(
        createElement(
          tag,
          { key: `h-${lineIndex}`, className: headingClassName(level) },
          ...renderInline(headingMatch[2], `h-${lineIndex}`),
        ),
      );
      lineIndex++;
      continue;
    }

    const bullet = line.match(/^- (.+)$/);
    const numbered = line.match(/^\d+\. (.+)$/);
    if (bullet && bullet[1] !== undefined) {
      flushNumbered();
      bulletGroup.push(createElement('li', { key: `bli-${bulletGroup.length}` }, ...renderInline(bullet[1], `bullet-${lineIndex}`)));
    } else if (numbered && numbered[1] !== undefined) {
      flushBullet();
      numberedGroup.push(createElement('li', { key: `nli-${numberedGroup.length}` }, ...renderInline(numbered[1], `numbered-${lineIndex}`)));
    } else {
      flushLists();
      if (line.trim() === '') {
        out.push(createElement('br', { key: `br-${lineIndex}` }));
      } else {
        out.push(createElement('p', { key: `p-${lineIndex}`, className: 'my-1' }, ...renderInline(line, `p-${lineIndex}`)));
      }
    }
    lineIndex++;
  }
  flushBullet();
  flushNumbered();
  return out;
}
